import axios from "axios";
import Format from "string-format";
import { batch } from "react-redux";
import {
  SET_AUTH_INIT_DATA,
  USER_LOGGED_OUT,
  SET_NONCE,
  SET_USER_DATA,
  UPDATE_WORKSPACE_DATA,
  SET_WORKSPACES_DATA,
  SET_SELECTED_WORKSPACE_ID,
} from "./types";
import {
  REGISTER_USER,
  LOGIN_USER,
  LOGIN_WITH_GOOGLE,
  RESET_PASSWORD,
  WORKSPACES,
  SHOW_MY_TEAM_MEMBERS,
  UPDATE_WORKSPACE,
} from "./endpoints";
import * as errorHandlerActions from "./errorHandlerActions";
import {
  REGISTRATION_EMAIL_SENT,
  PASSWORD_RESET_EMAIL_SENT,
  CREATE_WORKSPACE,
  LOGIN,
} from "../_utils/routes";
import {
  analyticsTrackEvent,
  setAxiosAuthHeaders,
  setAuthToken,
} from "../_utils/common";
import { redirect } from "./commonAction";
import {
  setSelectedSocialAccounts,
  setSelectedSocialAccountsForDrafts,
  setSocialAccounts,
} from "./socialAccountsActions";
import { clearPersistedStore, savePersistedStore } from "../store";

// REGISTER USER
/// Reason we need pass 'history' , is because we cannot push to the next page(Login here),
/// from an action like we can from a component.
export const registerUser = (userData, props, signalToken) => (dispatch) => {
  return axios
    .post(REGISTER_USER, userData, {
      cancelToken: signalToken,
    })
    .then((res) => {
      analyticsTrackEvent("signUp", {
        logInType: "standard",
        email: userData.email,
      });
      if (userData.plan_name) {
        const loginData = {
          email: userData.email,
          password: userData.password1,
        };
        dispatch(loginUser(loginData, props));
      } else {
        props.history.push(
          `${REGISTRATION_EMAIL_SENT}?email=${encodeURIComponent(
            userData.email
          )}`
        );
      }
    })
    .catch((error) => {
      dispatch(errorHandlerActions.handleHTTPError(error, props));
    });
};

// Login with email
export const loginUser = (userData, props) => (dispatch) => {
  return axios
    .post(LOGIN_USER, userData)
    .then((res) => {
      dispatch(processLogin(res.data, props));

      // GA - Custom events
      analyticsTrackEvent("login", {
        logInType: "standard",
      });
    })
    .catch((error) =>
      dispatch(errorHandlerActions.handleHTTPError(error, props))
    );
};

// Login with Google
export const loginWithGoogle =
  (tokenJSON, props, action = "login") =>
  (dispatch) => {
    return axios
      .post(LOGIN_WITH_GOOGLE, tokenJSON)
      .then((res) => {
        dispatch(processLogin(res.data, props));

        // GA - Custom events
        analyticsTrackEvent(action, {
          logInType: "Google",
        });
      })
      .catch((err) => {
        dispatch(errorHandlerActions.handleHTTPError(err, props));
      });
  };

// Save token, user and orgs
export const processLogin =
  (data, props, redirectTo = null) =>
  (dispatch) => {
    const { key, user } = data;
    const { orgs, ...userData } = user;

    setAuthToken(key);

    if (orgs.length > 0) {
      const workspaceId = orgs[0].id;

      setAxiosAuthHeaders(key, workspaceId);

      dispatch(
        setAuthInitData({
          user: userData,
          orgs,
          selectedOrg: workspaceId,
        })
      );
      if (redirectTo) {
        dispatch(redirect(redirectTo, props));
      }
    } else {
      setAxiosAuthHeaders(key);

      batch(() => {
        dispatch(
          setAuthInitData({
            user: userData,
            orgs: null,
            selectedOrg: null,
          })
        );
        dispatch(redirect(CREATE_WORKSPACE, props));
      });
    }
  };

export const logoutUser =
  (redirectUrl = LOGIN) =>
  (dispatch) => {
    // Remove token from local storage
    setAuthToken();

    // Remove auth header for future requests
    setAxiosAuthHeaders();

    // Clear redux persist
    clearPersistedStore();

    dispatch({
      type: USER_LOGGED_OUT,
      payload: null,
    });

    window.location.href = redirectUrl;
  };

export const switchWorkspace = (workspaceId, token) => (dispatch) => {
  return new Promise((resolve) => {
    setAxiosAuthHeaders(token, workspaceId);

    batch(() => {
      dispatch(setSocialAccounts([]));
      dispatch(setSelectedSocialAccounts([]));
      dispatch(setSelectedSocialAccountsForDrafts([]));
      dispatch(setSelectedWorkspaceId(workspaceId));
    });
    savePersistedStore();

    resolve();
  });
};

export const resetPassword = (userData, props) => (dispatch) => {
  axios
    .post(RESET_PASSWORD, userData)
    .then((res) => {
      if (res.status === 200) {
        props.history.push(
          `${PASSWORD_RESET_EMAIL_SENT}?email=${encodeURIComponent(
            userData.email
          )}`
        );
      }
    })
    .catch(function (error) {})
    .catch((error) =>
      dispatch(errorHandlerActions.handleHTTPError(error, props))
    );
};

export const setNonce = (nonce) => (dispatch) => {
  dispatch({
    type: SET_NONCE,
    payload: nonce,
  });
};

export const setAuthInitData = (payload) => (dispatch) => {
  dispatch({
    type: SET_AUTH_INIT_DATA,
    payload: payload,
  });
};

export const setUserData = (payload) => (dispatch) => {
  dispatch({
    type: SET_USER_DATA,
    payload: payload,
  });
};

export const setWorkspacesData = (payload) => (dispatch) => {
  dispatch({
    type: SET_WORKSPACES_DATA,
    payload: payload,
  });
};

export const setSelectedWorkspaceId = (payload) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_WORKSPACE_ID,
    payload: payload,
  });
};

export const updateWorkspace = (workspaceId, values) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .patch(Format(UPDATE_WORKSPACE, workspaceId), values, { params: { expand: "settings,subscription,members_count" } })
      .then((response) => {
        dispatch({
          type: UPDATE_WORKSPACE_DATA,
          payload: response.data,
        });

        resolve(response);
      })
      .catch((error) => {
        dispatch(errorHandlerActions.handleHTTPError(error));
        reject(error);
      });
  });
};

export const syncWorkspaces = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(WORKSPACES, { params: { expand: "settings,subscription,members_count" } })
      .then((res) => {
        // remove the role object to get the same org objects as in the /user request
        const workspaces = res.data.results?.map(({ role, ...item }) => item);

        dispatch(setWorkspacesData(workspaces));
        resolve(workspaces);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getWorkspaceMembers = (organisation) =>
  new Promise((resolve, reject) => {
    axios
      .get(`${SHOW_MY_TEAM_MEMBERS}${organisation}/members`)
      .then((res) => resolve(res.data.results))
      .catch((error) => {
        reject(error);
      });
  });
